import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

const EMPTY = { name: '', cif: '', responsible: '', email: '', phone: '', isLoginEnabled: true, licenses: 0 };

const CompanyModal = ({ open, initial, onClose, onSubmit }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        name: initial.name || '',
        cif: initial.cif || '',
        responsible: initial.responsible || '',
        email: initial.email || '',
        phone: initial.phone || '',
        isLoginEnabled: initial.isLoginEnabled ?? true,
        licenses: Number(initial.licenses) || 0,
      } : EMPTY);
      setErrors({});
      setApiError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Obligatorio';
    if (!form.cif.trim()) e.cif = 'Obligatorio';
    if (!form.responsible.trim()) e.responsible = 'Obligatorio';
    if (!form.email.trim()) e.email = 'Obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email no válido';
    if (!form.phone.trim()) e.phone = 'Obligatorio';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setApiError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        cif: form.cif.trim().toUpperCase(),
        responsible: form.responsible.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isLoginEnabled: form.isLoginEnabled,
        licenses: Math.max(0, Number(form.licenses) || 0),
      });
      onClose();
    } catch (err) {
      setApiError(err.message || 'Error al crear la empresa.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (key) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none text-sm transition-colors ${
      errors[key] ? 'border-danger focus:border-danger' : 'border-slate-200 focus:border-primary'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{isEdit ? 'Editar' : 'Alta'}</div>
            <h2 className="font-display text-lg font-bold text-ink">{isEdit ? 'Editar empresa' : 'Nueva empresa'}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Razón social</label>
            <input type="text" value={form.name} onChange={set('name')} className={inputCls('name')} autoFocus />
            {errors.name && <div className="text-xs text-danger mt-1">{errors.name}</div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">CIF</label>
              <input type="text" value={form.cif} onChange={set('cif')} className={inputCls('cif')} placeholder="B12345678" />
              {errors.cif && <div className="text-xs text-danger mt-1">{errors.cif}</div>}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Teléfono</label>
              <input type="tel" value={form.phone} onChange={set('phone')} className={inputCls('phone')} placeholder="+34 600 000 000" />
              {errors.phone && <div className="text-xs text-danger mt-1">{errors.phone}</div>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Responsable de contacto</label>
            <input type="text" value={form.responsible} onChange={set('responsible')} className={inputCls('responsible')} />
            {errors.responsible && <div className="text-xs text-danger mt-1">{errors.responsible}</div>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Email del responsable</label>
            <input type="email" value={form.email} onChange={set('email')} className={inputCls('email')} placeholder="nombre@empresa.com" />
            {errors.email && <div className="text-xs text-danger mt-1">{errors.email}</div>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Licencias activas</label>
            <input
              type="number"
              value={form.licenses}
              onChange={(e) => setForm((f) => ({ ...f, licenses: Number(e.target.value) || 0 }))}
              className={inputCls('licenses')}
              min="0"
              max="9999"
              placeholder="0 = sin límite"
            />
            <div className="text-[11px] text-ink-muted mt-1 leading-snug">
              Número máximo de empleados activos (con login habilitado) que puede tener la empresa.
              <strong className="text-ink"> 0 = sin límite.</strong> Si al crear un nuevo empleado se supera el cupo,
              se creará como inactivo automáticamente.
            </div>
          </div>

          <label className="flex items-center gap-3 pt-2 cursor-pointer">
            <input type="checkbox" checked={form.isLoginEnabled} onChange={toggle('isLoginEnabled')} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
            <div>
              <div className="text-sm font-semibold text-ink">Login habilitado</div>
              <div className="text-xs text-ink-muted">Si se desactiva, ningún usuario de esta empresa podrá acceder. Sus datos y registros se conservan.</div>
            </div>
          </label>

          {apiError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">
              {apiError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear empresa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyModal;
